/**
 * Read-only annual targets modal (القيم المستهدفة سنوياً) for KPI detail.
 * Matches Figma node 7569:213059: per-year blocks with a side year badge, each
 * period row showing المحقق (achieved) | المستهدف (target) | period-label as
 * tinted chips, an الإجمالي السنوي total row per year, and a grand المستهدف الكلي
 * row. Targets come from the server-authored `kpi.target_values`; achieved comes
 * from `kpi.annual_achieved` (engine per-period cumulative) — dash when absent.
 */
import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Modal, ScrollView, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { tokens } from '@design';
import { FONTS } from '../../../constants/config';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getTextAlign } from '../../../utils/rtl';
import type { KPI, TargetAggregation } from '../../../types';
import { yearlyTotal } from '../../../utils/targetGrid';

interface AnnualTargetModalProps {
  visible: boolean;
  onClose: () => void;
  kpi: KPI;
}

// slate-700 (#334155) alpha variants from the Figma cell fills (not standalone
// tokens — opacity steps of the slate-700 swatch in design/tokens.ts).
const CELL_BG = 'rgba(51,65,85,0.1)';
const TOTAL_BG = 'rgba(51,65,85,0.2)';

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

interface RowData {
  key: string;
  label: string;
  target: number;
  achieved: number | null;
}

export default function AnnualTargetModal({ visible, onClose, kpi }: AnnualTargetModalProps) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL, colors), [isRTL, colors]);

  const unit = kpi.unit || '';
  const aggregation = (kpi.target_aggregation || 'sum') as TargetAggregation;

  // Achieved value per period key "year:index" from the engine-authored list.
  const achievedMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const a of kpi.annual_achieved || []) {
      map[`${a.year}:${a.period_index}`] = parseFloat(a.value) || 0;
    }
    return map;
  }, [kpi.annual_achieved]);

  const { years, grandTarget, grandAchieved, hasAnyAchieved } = useMemo(() => {
    const cells = kpi.target_values || [];
    const byYear = new Map<number, RowData[]>();
    for (const c of cells) {
      const key = `${c.year}:${c.period_index}`;
      const target = parseFloat(c.value) || 0;
      const achieved = key in achievedMap ? achievedMap[key] : null;
      const arr = byYear.get(c.year) || [];
      arr.push({ key, label: c.period_label, target, achieved });
      byYear.set(c.year, arr);
    }
    const yearList = Array.from(byYear.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([year, rows]) => {
        const targets = rows.map((r) => r.target);
        const anyAchieved = rows.some((r) => r.achieved != null);
        const achievedVals = rows.map((r) => r.achieved ?? 0);
        return {
          year,
          rows,
          targetTotal: yearlyTotal(targets, unit, aggregation),
          achievedTotal: anyAchieved ? yearlyTotal(achievedVals, unit, aggregation) : null,
        };
      });
    const allTargets = cells.map((c) => parseFloat(c.value) || 0);
    const anyAch = Object.keys(achievedMap).length > 0;
    const grandAch =
      kpi.total_achieved != null ? parseFloat(kpi.total_achieved) || 0 : null;
    return {
      years: yearList,
      grandTarget: yearlyTotal(allTargets, unit, aggregation),
      grandAchieved: grandAch,
      hasAnyAchieved: anyAch,
    };
  }, [kpi.target_values, kpi.total_achieved, achievedMap, unit, aggregation]);

  const hasGrid = (kpi.target_values || []).length > 0;
  const dash = '—';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity style={styles.content} activeOpacity={1} onPress={() => {}}>
          <View style={styles.header}>
            <TouchableOpacity onPress={onClose} accessibilityLabel={t('common.close')}>
              <Text style={styles.close}>✕</Text>
            </TouchableOpacity>
            <Text style={styles.title}>{t('kpis.annualTargets')}</Text>
          </View>

          {!hasGrid ? (
            <View style={styles.notice}>
              <Text style={styles.noticeText}>{t('kpis.noAnnualTargets')}</Text>
            </View>
          ) : (
            <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
              {/* Column headers: المحقق | المستهدف above the two value columns */}
              <View style={styles.headerRow}>
                <View style={styles.cellGroup}>
                  <Text style={[styles.cell, styles.headerCellText]}>{t('kpis.achievedColumn')}</Text>
                  <Text style={[styles.cell, styles.headerCellText]}>{t('kpis.targetColumn')}</Text>
                  <Text style={[styles.cell, styles.headerCellText]} />
                </View>
                <View style={styles.badgeSpacer} />
              </View>

              {years.map((y) => (
                <View key={y.year} style={styles.yearBlock}>
                  <View style={styles.rowsCol}>
                    {y.rows.map((r) => (
                      <View key={r.key} style={styles.row}>
                        <Text style={[styles.cell, styles.valCell]}>
                          {r.achieved == null ? dash : fmt(r.achieved)}
                        </Text>
                        <Text style={[styles.cell, styles.valCell]}>{fmt(r.target)}</Text>
                        <Text style={[styles.cell, styles.labelCell]}>{r.label}</Text>
                      </View>
                    ))}
                    <View style={styles.row}>
                      <Text style={[styles.cell, styles.totalCell]}>
                        {y.achievedTotal == null ? dash : fmt(y.achievedTotal)}
                      </Text>
                      <Text style={[styles.cell, styles.totalCell]}>{fmt(y.targetTotal)}</Text>
                      <Text style={[styles.cell, styles.totalCell]}>{t('kpis.annualTotalRow')}</Text>
                    </View>
                  </View>
                  <View style={styles.yearBadge}>
                    <Text style={styles.yearBadgeText}>{y.year}</Text>
                  </View>
                </View>
              ))}

              {/* Grand total: المستهدف الكلي */}
              <View style={styles.grandRow}>
                <View style={styles.cellGroup}>
                  <Text style={[styles.cell, styles.totalCell]}>
                    {grandAchieved == null || !hasAnyAchieved ? dash : fmt(grandAchieved)}
                  </Text>
                  <Text style={[styles.cell, styles.totalCell]}>{fmt(grandTarget)}</Text>
                  <Text style={[styles.cell, styles.totalCell]}>{t('kpis.overallTarget')}</Text>
                </View>
                <View style={styles.badgeSpacer} />
              </View>
            </ScrollView>
          )}
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
}

const getStyles = (isRTL: boolean, colors: any) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: tokens.spacing.absolute['4'],
    },
    content: {
      width: '100%',
      maxWidth: 560,
      maxHeight: '85%',
      backgroundColor: colors.card,
      borderWidth: tokens.spacing.absolute['px'],
      borderColor: colors.border,
      borderRadius: tokens.radii.absolute['radius-12'],
      overflow: 'hidden',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: tokens.spacing.absolute['4'],
    },
    title: {
      flex: 1,
      fontSize: tokens.typography['heading 4']['font size'],
      fontFamily: FONTS.bold,
      color: colors.textPrimary,
      textAlign: getTextAlign(isRTL),
    },
    close: {
      fontSize: tokens.typography.paragraph['paragraph font size'],
      color: colors.textMuted,
      paddingHorizontal: tokens.spacing.absolute['2'],
    },
    body: {
      paddingHorizontal: tokens.spacing.absolute['6'],
    },
    bodyContent: {
      gap: tokens.spacing.absolute['5'],
      paddingBottom: tokens.spacing.absolute['5'],
    },
    notice: {
      padding: tokens.spacing.absolute['6'],
    },
    noticeText: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.regular,
      color: colors.textMuted,
      textAlign: 'center',
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: tokens.spacing.absolute['6'],
    },
    yearBlock: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: tokens.spacing.absolute['6'],
    },
    rowsCol: {
      flex: 1,
      gap: tokens.spacing.absolute['2'],
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: tokens.spacing.absolute['2'],
    },
    cellGroup: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: tokens.spacing.absolute['2'],
    },
    cell: {
      flex: 1,
      paddingHorizontal: tokens.spacing.absolute['1'],
      paddingVertical: tokens.spacing.absolute['px'],
      borderRadius: tokens.radii.absolute['radius-4'],
      fontSize: tokens.typography.paragraph['paragraph mini font size'],
      fontFamily: FONTS.regular,
      color: colors.textPrimary,
      textAlign: getTextAlign(isRTL),
    },
    valCell: {
      backgroundColor: CELL_BG,
    },
    labelCell: {
      backgroundColor: colors.border,
    },
    totalCell: {
      backgroundColor: TOTAL_BG,
    },
    headerCellText: {
      backgroundColor: 'transparent',
      color: colors.textMuted,
    },
    badgeSpacer: {
      width: 80,
    },
    yearBadge: {
      width: 80,
      minHeight: 24,
      backgroundColor: colors.border,
      borderRadius: tokens.radii.absolute['radius-8'],
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: tokens.spacing.absolute['2'],
      paddingVertical: tokens.spacing.absolute['px'],
    },
    yearBadgeText: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.regular,
      color: colors.textPrimary,
      textAlign: 'center',
    },
    grandRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: tokens.spacing.absolute['6'],
    },
  });
